import type { DashboardConfig } from '@/api/dashboardsApi'
import type { StreamAccessFilter } from '@/rbac/types'
import { asList } from '@/utils/asList'
import { isSystemColumn } from '@/utils/streamColumns'

/**
 * Streams + columns a dashboard reads, per panel type. Field access mirrors
 * panelRequestBuilders.ts — update both when panel shapes change.
 */

type UnknownRecord = Record<string, unknown>

function asString(value: unknown): string | null {
	return typeof value === 'string' && value.length > 0 ? value : null
}

function addColumns(required: Map<string, Set<string>>, table: unknown, columns: unknown[]): void {
	const tableName = asString(table)
	if (!tableName) {
		return
	}
	const set = required.get(tableName) ?? new Set<string>()
	for (const column of columns) {
		const name = asString(column)
		if (name && !isSystemColumn(name)) {
			set.add(name)
		}
	}
	required.set(tableName, set)
}

function namedColumns(value: unknown): unknown[] {
	if (!Array.isArray(value)) {
		return []
	}
	return value.map(entry => (entry && typeof entry === 'object' ? (entry as UnknownRecord).name : null))
}

function collectPanel(required: Map<string, Set<string>>, panel: UnknownRecord): void {
	switch (panel.type) {
		case 'line_chart': {
			for (const key of ['timeseries', 'alternateAxisMap']) {
				const series = panel[key]
				if (!Array.isArray(series)) {
					continue
				}
				for (const entry of series) {
					if (entry && typeof entry === 'object') {
						const item = entry as UnknownRecord
						addColumns(required, item.table, [item.column])
					}
				}
			}
			return
		}
		case 'state_timeline':
			addColumns(required, panel.table, [panel.stateColumn])
			return
		case 'aggregate_value':
		case 'histogram':
			addColumns(required, panel.table, [panel.column])
			return
		case 'bar_chart':
			addColumns(required, panel.table, [panel.aggregateColumn, panel.group, panel.subGroup])
			return
		case 'gauge_chart':
		case 'last_value':
		case 'led_panel':
			addColumns(required, panel.table, namedColumns(panel.columns))
			return
		case 'locate_devices':
		case 'track_devices': {
			addColumns(required, panel.table, [panel.latitude_column, panel.longitude_column])
			if (Array.isArray(panel.deviceStateKeys)) {
				for (const entry of panel.deviceStateKeys) {
					if (entry && typeof entry === 'object') {
						const item = entry as UnknownRecord
						addColumns(required, item.stream_name, [item.field_name])
					}
				}
			}
			addColumns(required, panel.stateStream, [panel.stateColumn])
			return
		}
		case 'stream_status': {
			if (Array.isArray(panel.streams)) {
				for (const stream of panel.streams) {
					addColumns(required, stream, [])
				}
			}
			return
		}
		case 'timeseries_table':
			addColumns(required, panel.table, asList(panel.columns))
			return
		case 'logs':
			addColumns(required, panel.table, ['level', 'tag', 'message'])
			return
		default:
			// static_text, iframe_panel, sessions, device_pulse: no stream reads
			return
	}
}

/** Map of stream name -> columns the dashboard reads from it (sorted, deduped). */
export function extractRequiredStreamColumns(config: DashboardConfig): Record<string, string[]> {
	const required = new Map<string, Set<string>>()
	const panels = asList(config.panels)
	for (const panel of panels) {
		collectPanel(required, panel as unknown as UnknownRecord)
	}
	const result: Record<string, string[]> = {}
	for (const [table, columns] of [...required.entries()].sort(([a], [b]) => a.localeCompare(b))) {
		result[table] = [...columns].sort((a, b) => a.localeCompare(b))
	}
	return result
}

/** `config.streamDependencies` shape — sorted, deduped, ready to send to the backend as-is. */
export function buildStreamDependencies(config: DashboardConfig): { name: string; fields: string[] }[] {
	const required = extractRequiredStreamColumns(config)
	return Object.keys(required)
		.sort((a, b) => a.localeCompare(b))
		.map(name => ({ name, fields: required[name] }))
}

export interface StreamCoverageGap {
	table: string
	/** 'table' = the role cannot read the stream at all; otherwise the specific missing columns. */
	missing: 'table' | string[]
}

export function computeStreamCoverageGaps(
	required: Record<string, string[]>,
	streamRead: StreamAccessFilter | undefined,
): StreamCoverageGap[] {
	if (streamRead === 'all') {
		return []
	}
	const tables = Object.keys(required).sort((a, b) => a.localeCompare(b))
	if (streamRead === undefined) {
		return tables.map(table => ({ table, missing: 'table' as const }))
	}
	const gaps: StreamCoverageGap[] = []
	for (const table of tables) {
		const grant = streamRead.some[table]
		if (grant === undefined) {
			gaps.push({ table, missing: 'table' })
			continue
		}
		if (grant === 'all') {
			continue
		}
		const allowed = new Set(grant.some)
		const missingColumns = required[table].filter(column => !allowed.has(column))
		if (missingColumns.length > 0) {
			gaps.push({ table, missing: missingColumns })
		}
	}
	return gaps
}
